import { hasEntityEncodedHtmlMarkup } from "./encoding";

/** Plaintext convention `<https://…>` / `<mailto:…>` — not HTML tags. */
function isAngleBracketSchemeLink(tail: string): boolean {
  return /^<https?:\/\//i.test(tail) || /^<mailto:/i.test(tail);
}

/**
 * Whether `tail` (which must start at a `<` in the original string) opens markup the HTML
 * tokenizer would treat as a tag / markup declaration — approximation of
 * [WHATWG tag open state](https://html.spec.whatwg.org/multipage/parsing.html#tag-open-state).
 *
 * Excludes angle-bracket URLs (`<https://…>`) — callers should skip those separately.
 * Excludes plausible non-HTML uses such as `<filename.pdf>` (`.` immediately after the tag name).
 */
export function startsHtmlMarkupOpen(tail: string): boolean {
  if (!tail.startsWith("<")) {
    return false;
  }
  if (/^<!(?:DOCTYPE|--|\[CDATA\[)/i.test(tail)) {
    return true;
  }
  if (/^<\?/i.test(tail)) {
    return true;
  }
  return /^<\/?[a-z][a-z0-9:-]*(?:[\s/>]|$)/i.test(tail);
}

/**
 * First `<` that begins HTML markup (doctype, comments, tags), skipping bracketed URLs.
 */
export function findFirstHtmlMarkupIndex(body: string): number {
  let i = 0;
  while (i < body.length) {
    const lt = body.indexOf("<", i);
    if (lt === -1) {
      return -1;
    }
    const tail = body.slice(lt);
    if (isAngleBracketSchemeLink(tail)) {
      i = lt + 1;
      continue;
    }
    if (startsHtmlMarkupOpen(tail)) {
      return lt;
    }
    i = lt + 1;
  }
  return -1;
}

/** Detect stored HTML newsletters / rich mail. */
export function isHtmlContent(content: string): boolean {
  if (!content || typeof content !== "string") return false;
  const sample = content.slice(0, 9000).trim();

  if (hasEntityEncodedHtmlMarkup(sample)) return true;

  if (/^\s*<!DOCTYPE\b/i.test(sample)) return true;
  if (/^\s*<\?xml\b/i.test(sample)) return true;

  if (
    /^\s*<!--/.test(sample) &&
    /<(?:body|table|style|meta|tbody|thead|\/tr|div)\b/i.test(sample)
  ) {
    return true;
  }

  if (/<(?:html|head)\b[\s/>]/i.test(sample)) return true;
  if (/<(?:body|table|thead|tbody|style|meta|center|blockquote)\b/i.test(sample)) return true;

  const inlineAndStructuralTags =
    /<(?:html|body|div|p|span|strong|em|b|i|u|font|h[1-6]|ul|ol|li|table|tr|td|th|a|img|br|hr|nobr)[\s/>]/i;
  return inlineAndStructuralTags.test(sample);
}

/**
 * Many synced messages concatenate text/plain then text/html. If a long plaintext
 * prefix contains no tags and HTML begins later, drop the prefix so we render once.
 */
export function trimLeadingPlaintextBeforeHtml(body: string): string {
  if (!body || typeof body !== "string") {
    return "";
  }
  const idx = findFirstHtmlMarkupIndex(body);
  if (idx === -1 || idx < 120) {
    return body;
  }
  return body.slice(idx);
}
